/**
 * API Cache System
 * Provides intelligent caching with TTL and cleanup
 */

import { ApiCache, CacheEntry, CacheStats } from '@/types/api';

/**
 * In-memory cache implementation with TTL support
 */
export class MemoryCache implements ApiCache {
  private cache = new Map<string, CacheEntry<any>>();
  private stats = {
    totalHits: 0,
    totalMisses: 0,
    totalSets: 0
  };
  
  private cleanupInterval: NodeJS.Timeout;
  private readonly defaultTTL = 5 * 60 * 1000; // 5 minutes
  private readonly maxEntries = 1000;
  
  constructor() {
    // Run cleanup every minute
    this.cleanupInterval = setInterval(() => this.cleanup(), 60 * 1000);
  }
  
  /**
   * Get cached data
   */
  get<T>(key: string): CacheEntry<T> | null {
    const entry = this.cache.get(key);
    
    if (!entry) {
      this.stats.totalMisses++;
      return null;
    }
    
    // Check if entry has expired
    const now = new Date();
    const expiresAt = new Date(entry.timestamp.getTime() + entry.ttl);
    
    if (now > expiresAt) {
      this.cache.delete(key);
      this.stats.totalMisses++;
      return null;
    }
    
    this.stats.totalHits++;
    return entry;
  }
  
  /**
   * Set cached data
   */
  set<T>(key: string, data: T, ttl?: number): void {
    // Enforce max entries limit
    if (this.cache.size >= this.maxEntries) {
      this.evictOldest();
    }
    
    const entry: CacheEntry<T> = {
      data,
      timestamp: new Date(),
      ttl: ttl || this.defaultTTL,
      key
    };
    
    this.cache.set(key, entry);
    this.stats.totalSets++;
  }
  
  /**
   * Delete cached data
   */
  delete(key: string): void {
    this.cache.delete(key);
  }
  
  /**
   * Clear all cached data
   */
  clear(): void {
    this.cache.clear();
  }
  
  /**
   * Clean up expired entries
   */
  cleanup(): void {
    const now = new Date();
    const keysToDelete: string[] = [];
    
    for (const [key, entry] of this.cache.entries()) {
      const expiresAt = new Date(entry.timestamp.getTime() + entry.ttl);
      if (now > expiresAt) {
        keysToDelete.push(key);
      }
    }
    
    keysToDelete.forEach(key => this.cache.delete(key));
    
    if (keysToDelete.length > 0) {
      console.log(`Cache cleanup: removed ${keysToDelete.length} expired entries`);
    }
  }
  
  /**
   * Get cache statistics
   */
  getStats(): CacheStats {
    const totalRequests = this.stats.totalHits + this.stats.totalMisses;
    const hitRate = totalRequests > 0 ? this.stats.totalHits / totalRequests : 0;
    const missRate = totalRequests > 0 ? this.stats.totalMisses / totalRequests : 0;
    
    // Estimate memory usage (rough calculation)
    const memoryUsage = this.estimateMemoryUsage();
    
    return {
      totalEntries: this.cache.size,
      hitRate,
      missRate,
      totalHits: this.stats.totalHits,
      totalMisses: this.stats.totalMisses,
      memoryUsage
    };
  }
  
  /**
   * Evict oldest entry when cache is full
   */
  private evictOldest(): void {
    let oldestKey: string | null = null;
    let oldestTime = new Date();
    
    for (const [key, entry] of this.cache.entries()) {
      if (entry.timestamp < oldestTime) {
        oldestTime = entry.timestamp;
        oldestKey = key;
      }
    }
    
    if (oldestKey) {
      this.cache.delete(oldestKey);
    }
  }
  
  /**
   * Estimate memory usage (rough calculation)
   */
  private estimateMemoryUsage(): number {
    let totalSize = 0;
    
    for (const [key, entry] of this.cache.entries()) {
      // Rough estimation: key size + JSON string size of data
      totalSize += key.length * 2; // UTF-16 characters
      totalSize += JSON.stringify(entry.data).length * 2;
      totalSize += 100; // Overhead for entry metadata
    }
    
    return totalSize;
  }
  
  /**
   * Destroy cache and cleanup resources
   */
  destroy(): void {
    clearInterval(this.cleanupInterval);
    this.clear();
  }
}

/**
 * Cache key generation utilities
 */
export class CacheKeyGenerator {
  /**
   * Generate cache key for asset price
   */
  static assetPrice(assetId: string): string {
    return `price:${assetId}`;
  }
  
  /**
   * Generate cache key for multiple asset prices
   */
  static assetPrices(assetIds: string[]): string {
    const sortedIds = [...assetIds].sort();
    return `prices:${sortedIds.join(',')}`;
  }
  
  /**
   * Generate cache key for historical data (include interval when D/W/M so cache is separate)
   */
  static historicalData(assetId: string, timeRange: string, interval?: string): string {
    return interval ? `history:${assetId}:${timeRange}:${interval}` : `history:${assetId}:${timeRange}`;
  }
  
  /**
   * Generate cache key for asset search
   */
  static assetSearch(query: string, type?: string): string {
    return `search:${query}${type ? `:${type}` : ''}`;
  }
  
  /**
   * Generate cache key for asset validation
   */
  static assetValidation(symbol: string, type: string): string {
    return `validate:${symbol}:${type}`;
  }
  
  /**
   * Generate cache key for API test
   */
  static apiTest(provider: string, keyHash: string): string {
    return `test:${provider}:${keyHash}`;
  }
}

/**
 * Cache configuration for different data types
 */
export const CacheConfig = {
  // Asset prices - short TTL for real-time data
  ASSET_PRICE: 30 * 1000, // 30 seconds
  
  // Historical data - longer TTL as it doesn't change frequently
  HISTORICAL_DATA: 5 * 60 * 1000, // 5 minutes
  
  // Search results - medium TTL
  SEARCH_RESULTS: 10 * 60 * 1000, // 10 minutes
  
  // Asset validation - long TTL as asset info is stable
  ASSET_VALIDATION: 60 * 60 * 1000, // 1 hour
  
  // API tests - short TTL to ensure fresh validation
  API_TEST: 60 * 1000, // 1 minute
} as const;

// Export singleton instance
export const apiCache = new MemoryCache();